<template>
  <!-- Only show the child table when not collapsed -->
  <div v-show="!collapsed" class="child-activity-wrapper">
    <table class="table table-bordered table-striped table-hover child-activity-table">
      <thead class="table-light">
        <tr>
          <th
            v-for="(col, index) in mappingColumns.configs"
            :key="index"
            :style="{ textAlign: col.textAlign || 'center' }"
            class="align-middle"
          >
            <template v-if="col.name === 'checkbox'">
              <input type="checkbox" :checked="isAllSelected" @change="toggleSelectAll" />
            </template>
            <template v-else>
              {{ col.label }}
            </template>
          </th>
        </tr>
      </thead>
      <tbody>
        <tr v-if="displayActivities.length === 0">
          <td :colspan="mappingColumns.configs.length" class="text-center fw-bold py-3">
            No activities available
          </td>
        </tr>
        <tr v-for="(activity, index) in displayActivities" :key="activity['Activity Id'] || index">
          <td
            v-for="(col, i) in mappingColumns.configs"
            :key="i"
            :style="{ textAlign: col.textAlign || 'center' }"
          >
            <template v-if="col.name === 'checkbox'">
              <input
                type="checkbox"
                :value="activity['Activity Id']"
                :checked="globalSelectedActivityIds.includes(activity['Activity Id'])"
                @change="toggleRowSelection($event, activity['Activity Id'])"
              />
            </template>
            <template v-else>
              <div v-if="shouldTruncate(col.label, activity[col.name])">
                <span>
                  {{ truncateText(activity[col.name], 60) }}
                </span>
                <a href="#" @mouseover="showPopover($event, activity[col.name])" @mouseleave="hidePopover" style="font-size: inherit;">...</a>
              </div>
              <div v-else>
                {{ activity[col.name] }}
              </div>
            </template>
          </td>
        </tr>
      </tbody>
    </table>
  </div>
  <transition name="fade">
    <div
      v-if="globalSelectedActivityIds.length > 0"
      style="position: fixed; bottom: -16px; left: 50%; transform: translateX(-50%); display: flex; align-items: center; padding: 0;"
    >
      <div class="me-2 p-3">
        Activities Selected: <span>{{ globalSelectedActivityIds.length }}</span>
      </div>
      <button class="btn" @click="openViewPopup">View</button>
      <button @click="cancelSelection" class="btn">Clear</button>
    </div>
  </transition>
  <transition name="fade">
    <ViewChildPopup
      v-if="showViewPopup"
      :selectedActivities="selectedActivities"
      :childActivityViewData="childActivityViewData"
      :requestId="requestId"
      @close="closeViewPopup"
      @add-activities="handleAddActivities"
      @clear-selection="clearSelectedActivities"
    />
  </transition>
</template>

<script>
import { type, pageViews } from "../../utils/util";
import Store from "../../vuex/store.js";
import ViewChildPopup from "../ViewChildPopup.vue";
import axios from "axios"; // Add axios import if not already present
import { getCookie } from "../../store/cookie.js";
import { makeOpearationRequest } from "@/services/apiServices";
import { Popover } from 'bootstrap';


export default {
  name: pageViews.CHILD_ACTIVITIES_VIEW,
  components: { ViewChildPopup },
  props: {
    activities: { type: Array, default: () => [] },
    collapsed: { type: Boolean, default: false },
    requestId: { type: String, required: true }
  },
  data() {
    return {
      showViewPopup: false,
      mappingColumns: {
        type: type.CHILD_ACTIVITY, // Ensure this matches the backend expectation
        isMultiSelectionAllowed: true,
        defaultSortOrder: { name: "asc", label: "Ascending" },
        defaultSortColumn: { name: "Process", label: "Process" },
        configs: [
          { name: "checkbox", label: "Select", order: 0, isSortable: false, defaultSelected: false, textAlign: "center" },
          { name: "Activity", label: "Activity", order: 4, isSortable: true, defaultSelected: true, textAlign: "left" },
          { name: "Activity Type", label: "Activity Type", order: 5, isSortable: true, defaultSelected: true, textAlign: "left" },
          { name: "Is Starter", label: "Is Starter", order: 6, isSortable: true, defaultSelected: true, textAlign: "left" },
          { name: "Input", label: "Input", order: 8, isSortable: true, defaultSelected: true, textAlign: "left" },
          { name: "Output", label: "Output", order: 9, isSortable: true, defaultSelected: true, textAlign: "left" },
          { name: "Supporting Process Location", label: "Supporting Process Location", order: 10, isSortable: true, defaultSelected: true, textAlign: "left" },
          { name: "Supporting Process", label: "Supporting Process", order: 11, isSortable: true, defaultSelected: true, textAlign: "left" }
        ]
      },
      childActivityViewData: null // Store API response here
    };
  },
  computed: {
    displayActivities() {
      // Always return an array
      return Array.isArray(this.activities) ? this.activities : [];
    },
    globalSelectedActivityIds() {
      return Store.state.selectedActivityIds;
    },
    isAllSelected() {
      return (
        this.displayActivities.length > 0 &&
        this.displayActivities.every(act => this.globalSelectedActivityIds.includes(act["Activity Id"]))
      );
    },
    selectedActivities() {
      return this.displayActivities.filter(act => this.globalSelectedActivityIds.includes(act["Activity Id"]));
    }
  },
  watch: {
    activities(newVal) {
      console.log("activities prop changed:", newVal);
    }
  },
  methods: {
    toggleSelectAll(event) {
      if (event.target.checked) {
        this.displayActivities.forEach(act => {
          if (!this.globalSelectedActivityIds.includes(act["Activity Id"])) {
            Store.commit("addActivityId", act["Activity Id"]);
          }
        });
      } else {
        this.displayActivities.forEach(act => {
          if (this.globalSelectedActivityIds.includes(act["Activity Id"])) {
            Store.commit("removeActivityId", act["Activity Id"]);
          }
        });
      }
    },
    toggleRowSelection(event, activityId) {
      if (event.target.checked) {
        if (!this.globalSelectedActivityIds.includes(activityId)) {
          Store.commit("addActivityId", activityId);
        }
      } else {
        if (this.globalSelectedActivityIds.includes(activityId)) {
          Store.commit("removeActivityId", activityId);
        }
      }
    },
    cancelSelection() {
      Store.commit("clearSelectedActivities");
      this.closeViewPopup();
      this.$emit("collapse");
    },
    async fetchChildActivityView() {
      if (!this.globalSelectedActivityIds || this.globalSelectedActivityIds.length === 0) {
        console.warn("No activities selected for child activity view.");
        this.childActivityViewData = null;
        return;
      }
      const payload = {
        userId: getCookie("userId"),
        // activityIds: this.globalSelectedActivityIds,
        actvitiesIds: this.globalSelectedActivityIds,
        
        type: "MAPPER.CHILD_ACTIVITY_VIEW"
      };
      this.childActivityViewData = null;
      try {
        // Remove the undefined config argument
        console.log("Fetching child activity view with payload:", payload);
        const response = await makeOpearationRequest(this.$acclConfigs, "/childActivityView", payload);
        console.log("Child activity view response:", response.data);
        if (response.status === 200 && response.data) {
          this.childActivityViewData = response.data;
        }
      } catch (error) {
        console.error("Failed to fetch child activity view:", error);
        this.childActivityViewData = null;
      }
    },
    async openViewPopup() {
      await this.fetchChildActivityView();
      this.showViewPopup = true;
    },
    closeViewPopup() {
      this.showViewPopup = false;
    },
    handleAddActivities() {
      this.showViewPopup = false;
      // Optionally, clear any temporary data if needed
    },
    clearSelectedActivities() {
      Store.commit("clearSelectedActivities");
      this.$emit("collapse"); // Also close/collapse the child activities view
    },
    shouldTruncate(label, text) {
      const truncatableLabels = [
        'Usecase Location', 'Usecase', 'Process', 'Process Location', 'Sub Process', 'Sub Process Location',
        'Input Node', 'Binding Node', 'Condition', 'Condition Group', 'Input', 'Output',
        'Supporting Process Location', 'Supporting Process', 'Field Name', 'Reference Name',
        'Expression', 'Reference Field Name', 'Activity', 'Activity Type', 'Is Starter'
      ];
      return truncatableLabels.includes(label) && text && text.length > 60;
    },
    truncateText(text, length) {
      if (!text) return '';
      return text.length > length ? text.substring(0, length) : text;
    },
    showPopover(event, content) {
      const popover = new Popover(event.target, {
        content: content,
        trigger: 'hover',
        placement: 'auto',
        container: 'body',
        boundary: 'viewport',
        fallbackPlacements: ['top', 'bottom', 'left', 'right']
      });
      popover.show();
    },
    hidePopover(event) {
      const popover = Popover.getInstance(event.target);
      if (popover) {
        popover.hide();
      }
    },
  }
};
</script>

<style scoped>
    .child-activity-table {
      width: 100%;
      border-collapse: collapse;
      background-color: #fff;
    }
    .child-activity-table th {
      background-color: #da6a26;
      color: whitesmoke;
      border: 1px solid #dee2e6;
      padding: 0.75rem;
      vertical-align: middle;
    }
    .child-activity-table td {
      border: 1px solid #dee2e6;
      padding: 0.75rem;
      vertical-align: middle;
    }
    .table-header-sticky {
      position: sticky;
      top: 0;
      background: #f8f9fa;
      z-index: 1;
    }
    .fade-enter-active,
    .fade-leave-active {
      transition: opacity 0.3s ease;
    }
    .fade-enter-from,
    .fade-leave-to {
      opacity: 0;
    }
    .btn:hover {
      color: red;
    }
</style>